import { hydrateRoot, createRoot } from 'react-dom/client';
import { BrowserRouter, HashRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import App from './App';
import './index.css';
import { initializeRemoteLogger } from '@/lib/remote-logger';
import { registerServiceWorker } from '@/integrations/supabase/client';
import type { SsrPayload } from '@/types/ssr';

initializeRemoteLogger();

const isElectron = typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().includes('electron');
const Router = isElectron ? HashRouter : BrowserRouter;
const routerFuture = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
};

const payload: SsrPayload | null = typeof window !== 'undefined' ? window.__MAPLEOS_SSR__ || null : null;

function applyInitialDocumentState() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  if (window.location.pathname === '/' || window.location.pathname.startsWith('/docs') || window.location.pathname.startsWith('/downloads') || window.location.pathname.startsWith('/partnership') || window.location.pathname.startsWith('/news') || window.location.pathname.startsWith('/forums') || window.location.pathname.startsWith('/bugs') || window.location.pathname.startsWith('/products')) {
    document.documentElement.classList.add('allow-window-scroll');
  }

  const style = document.createElement('style');
  style.textContent = `
    .prose h1, .prose h2, .prose h3, .prose h4 {
      font-weight: 600;
      margin-top: 1.5em;
      margin-bottom: 0.5em;
    }
    .prose strong {
      font-weight: 600;
    }
    .prose p {
      margin-bottom: 1em;
    }
    .prose ol, .prose ul {
      margin-top: 0.5em;
      margin-bottom: 1em;
      padding-left: 1.5em;
    }
    .prose li {
      margin-bottom: 0.5em;
    }
    .prose a {
      color: hsl(var(--primary));
      text-decoration: underline;
    }
    .prose blockquote {
      border-left: 3px solid hsl(var(--border));
      padding-left: 1em;
      font-style: italic;
    }
  `;
  document.head.appendChild(style);
}

function setupServiceWorker() {
  if (typeof window === 'undefined' || isElectron) {
    return;
  }

  if (process.env.NODE_ENV === 'production') {
    registerServiceWorker().catch((error) => {
      console.error('[SW] Registration failed:', error);
    });
    return;
  }

  if ('serviceWorker' in navigator && typeof navigator.serviceWorker.getRegistrations === 'function') {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      for (const registration of registrations) {
        registration.unregister();
      }
    }).catch((error) => {
      console.warn('[SW] Failed to enumerate registrations in dev mode:', error);
    });
  }
}

applyInitialDocumentState();
setupServiceWorker();

const container = document.getElementById('root');

if (!container) {
  throw new Error('Root container not found');
}

const app = (
  <Router future={routerFuture}>
    <HelmetProvider>
      <App ssrPayload={payload} />
    </HelmetProvider>
  </Router>
);

if (container.hasChildNodes()) {
  hydrateRoot(container, app);
} else {
  createRoot(container).render(app);
}
